import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map, mergeMap, startWith } from 'rxjs/operators';

import { JSend } from '../../models/jsend.model';
import { PusherMessage } from '../../models/pusher-message.model';
import { PusherService } from '../../services/pusher.service';
import { buildFilters } from '../../utilities/filters';
import { ApproverDecision, DoA } from '../models/doa.model';

/**
 * Servicio para gestionar Delegaciones de Autorización (DoA - Delegation of Authorization).
 *
 * Las delegaciones de autorización son flujos de aprobación multi-paso donde documentos o entidades
 * requieren la aprobación secuencial o paralela de múltiples aprobadores.
 *
 * @description
 * Este servicio actúa como cliente del microservicio agree-doa y proporciona:
 * - Creación de delegaciones con aprobadores configurables
 * - Emisión de votos (APPROVED, DISAPPROVED, ABSTAINED)
 * - Consulta de delegaciones con filtros avanzados
 * - Suscripción en tiempo real a cambios via Pusher
 * - Paginación de resultados
 *
 * @example
 * ```typescript
 * // Crear una delegación
 * const doa = new DoA();
 * doa.entityKey = 'purchase_order';
 * doa.entityId = 'PO-12345';
 * doa.companyId = '123';
 * doa.approvers = [...];
 * this.doaService.create(doa).subscribe();
 *
 * // Votar en una delegación
 * const decision = new ApproverDecision();
 * decision.accountId = 456;
 * decision.vote = 'APPROVED';
 * decision.comment = 'Todo en orden';
 * this.doaService.vote(doa, decision).subscribe();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class DoAService {

  /** URL base del endpoint de delegaciones de autorización */
  private baseUrl = '/:apiBase/delegation-of-authority';

  /** URL para operaciones sobre una delegación específica */
  private delegationById = this.baseUrl + '/:id';

  /** URL para emitir un voto sobre una delegación */
  private delegationVote = this.delegationById + '/vote';

  /**
   * Mapeo de parámetros de filtros del frontend a query params del backend.
   *
   * @description
   * Convierte filtros en formato frontend a formato esperado por el API:
   * - status: PENDING, APPROVED, DISAPPROVED, RETIRED
   * - includeExpired: true/false - Incluir delegaciones vencidas
   * - entity: Tipo de entidad (ej: 'purchase_order', 'invoice')
   * - entityId: ID de la entidad específica
   * - companyId: ID de la empresa (requerido)
   * - pendingAccountId: Filtrar por cuenta con voto pendiente
   */
  private readonly queryMap: Record<string, string> = {
    'status': 'filters[status]',
    // 'accounts': 'filters[accounts]',
    'includeExpired': 'filters[includeExpired]',
    'entity': 'filters[entityKey]',
    'entityId': 'filters[entityId]',
    // 'vote': 'filters[vote]'
    'companyId': 'filters[companyId]',
    'pendingAccountId': 'filters[pendingAccountId]',
  };

  constructor(
    private http: HttpClient,
    private pusherService: PusherService
  ) { }

  /**
   * Obtiene una lista de delegaciones de autorización aplicando filtros.
   *
   * @private
   * @param filters - Objeto con criterios de filtrado:
   *   - status: Estado de la delegación (PENDING, APPROVED, DISAPPROVED, RETIRED)
   *   - companyId: ID de la empresa (requerido)
   *   - entity: Tipo de entidad (ej: 'purchase_order', 'invoice')
   *   - entityId: ID específico de la entidad
   *   - pendingAccountId: Filtrar por cuenta que tiene voto pendiente
   *   - includeExpired: true para incluir delegaciones vencidas
   *
   * @returns Observable con:
   *   - body: Array de delegaciones transformadas a instancias de DoA
   *   - headers: Headers HTTP con información de paginación (X-Total-Count, X-Per-Page, etc.)
   *
   * @description
   * Realiza GET a `/delegation-of-authority` con query params construidos dinámicamente.
   * Los headers de respuesta incluyen metadata de paginación según el estándar del backend.
   */
  private get(filters: any): Observable<{ body: DoA[], headers: HttpHeaders }> {
    let url = this.baseUrl;

    url = buildFilters(url, filters, this.queryMap);

    return this.http.get<JSend<{
      delegations: DoA[]
    }>>(url, { observe: 'response' }).pipe(map(response => {
      return { body: plainToInstance(DoA, response.body.data.delegations), headers: response.headers };
    }));
  }

  /**
   * Observa cambios en tiempo real de las delegaciones de un usuario específico.
   *
   * @param userId - ID del usuario cuyo canal privado de Pusher se suscribirá
   * @param filters - Filtros para aplicar al obtener las delegaciones
   *
   * @returns Observable que emite cada vez que:
   *   - Se suscribe inicialmente (carga inicial)
   *   - Pusher recibe un evento 'doa' en el canal `private_{userId}`
   *
   * @description
   * Conecta al canal Pusher privado del usuario para recibir notificaciones push en tiempo real
   * cuando ocurren cambios en delegaciones que le afectan:
   * - Nueva delegación donde es aprobador
   * - Alguien votó en una delegación donde está involucrado
   * - Delegación fue aprobada/rechazada/retirada
   *
   * Al recibir cualquier evento, re-ejecuta la consulta con los filtros especificados
   * para obtener el estado actualizado de todas las delegaciones.
   *
   * @example
   * ```typescript
   * this.doaService.watch(currentUser.id, { status: 'PENDING', companyId: '123' })
   *   .subscribe(result => {
   *     this.delegations = result.body;
   *     this.totalCount = result.headers.get('X-Total-Count');
   *   });
   * ```
   */
  public watch(userId: number, filters: any): Observable<{ body: DoA[], headers: HttpHeaders }> {
    return this.pusherService.listen(`private_${userId}`, 'doa').pipe(
      startWith({}),
      mergeMap(response => {
        return this.get(filters);
      })
    );
  }

  /**
   * Observa cambios en tiempo real de las delegaciones de una empresa específica.
   *
   * @param companyId - ID de la empresa cuyo canal de Pusher se suscribirá
   * @param filters - Filtros para aplicar al obtener las delegaciones
   *
   * @returns Observable que emite cada vez que:
   *   - Se suscribe inicialmente (carga inicial)
   *   - Pusher recibe un evento 'doa' en el canal `company_{companyId}`
   *
   * @description
   * Similar a `watch()` pero a nivel empresa. Conecta al canal Pusher de la empresa
   * para recibir notificaciones de todas las delegaciones de la compañía:
   * - Cualquier delegación creada en la empresa
   * - Votos emitidos por cualquier usuario de la empresa
   * - Cambios de estado en delegaciones de la empresa
   *
   * Útil para dashboards o vistas de administración que necesitan ver
   * todas las delegaciones de la empresa en tiempo real.
   *
   * @example
   * ```typescript
   * // Dashboard de admin viendo todas las delegaciones de la empresa
   * this.doaService.watchByCompany(123, { includeExpired: false })
   *   .subscribe(result => {
   *     this.allCompanyDelegations = result.body;
   *   });
   * ```
   */
  public watchByCompany(companyId: number, filters: any): Observable<{ body: DoA[], headers: HttpHeaders }> {
    return this.pusherService.listen(`company_${companyId}`, 'doa').pipe(
      startWith({}),
      mergeMap(response => {
        return this.get(filters);
      })
    );
  }

  /**
   * Crea una nueva delegación de autorización.
   *
   * @param doa - Objeto DoA con la configuración de la delegación:
   *   - companyId: ID de la empresa (requerido)
   *   - entityKey: Tipo de entidad (ej: 'purchase_order', 'invoice') (requerido)
   *   - entityId: ID de la entidad (requerido)
   *   - name: Nombre descriptivo de la delegación (opcional)
   *   - ordered: true para aprobación secuencial, false para paralela (default: true)
   *   - dueDate: Fecha de vencimiento (opcional)
   *   - approvers: Array de aprobadores con { accountId, step } (requerido, min 1, max 50)
   *
   * @returns Observable con la delegación creada incluyendo su ID (KSUID) generado
   *
   * @description
   * Crea una nueva delegación de autorización con procesamiento asíncrono:
   * 1. Backend genera un KSUID único
   * 2. Encola la creación en SQS
   * 3. Retorna el ID inmediatamente
   * 4. Procesamiento asíncrono crea la delegación en DB
   *
   * La delegación inicia en estado PENDING con currentVotingStep = 1.
   * Se envían notificaciones a los aprobadores del primer step.
   *
   * **Tipos de flujo:**
   * - ordered: true → Los aprobadores votan secuencialmente según su step
   * - ordered: false → Todos los aprobadores del mismo step pueden votar en paralelo
   *
   * @example
   * ```typescript
   * const newDoa = new DoA();
   * newDoa.companyId = '123';
   * newDoa.entityKey = 'purchase_order';
   * newDoa.entityId = 'PO-12345';
   * newDoa.name = 'Aprobación de Orden de Compra #12345';
   * newDoa.ordered = true; // Secuencial
   * newDoa.dueDate = new Date('2025-12-31');
   * newDoa.approvers = [
   *   { accountId: 1, step: 1 }, // Primer aprobador
   *   { accountId: 2, step: 2 }, // Segundo aprobador
   *   { accountId: 3, step: 2 }  // Tercer aprobador (mismo step que el segundo)
   * ];
   *
   * this.doaService.create(newDoa).subscribe(
   *   created => console.log('Delegación creada con ID:', created.id)
   * );
   * ```
   */
  public create(doa: DoA): Observable<DoA> {
    let url = this.baseUrl;

    return this.http.post<JSend<{
      delegation: DoA
    }>>(url, doa).pipe(map(response => {
      return plainToInstance(DoA, response.data.delegation);
    }));
  }

  /**
   * Obtiene una delegación específica por su ID.
   *
   * @private
   * @param id - KSUID de la delegación (formato: 27 caracteres alfanuméricos)
   *
   * @returns Observable con la delegación completa incluyendo:
   *   - Todos sus aprobadores con sus decisiones
   *   - Estado actual (PENDING, APPROVED, DISAPPROVED, RETIRED)
   *   - Step de votación actual
   *   - Metadata (fechas, entidad relacionada, etc.)
   *
   * @description
   * Realiza GET a `/delegation-of-authority/{id}` para obtener el detalle completo
   * de una delegación específica. Incluye toda la información de aprobadores,
   * sus votos (si ya votaron), comentarios y timestamps.
   */
  private getById(id: string): Observable<DoA> {
    let url = this.delegationById
      .replace(":id", id.toString());

    return this.http.get<JSend<{
      delegation: DoA
    }>>(url).pipe(map(response => {
      return plainToInstance(DoA, response.data.delegation);
    }));
  }

  /**
   * Observa cambios en tiempo real de una delegación específica.
   *
   * @param userId - ID del usuario cuyo canal privado de Pusher se suscribirá
   * @param id - KSUID de la delegación a observar
   *
   * @returns Observable que emite la delegación actualizada cuando:
   *   - Se suscribe inicialmente (carga inicial)
   *   - Pusher recibe un evento 'doa' relacionado con esta delegación específica
   *
   * @description
   * Conecta al canal Pusher privado del usuario y filtra eventos para observar
   * solo cambios en una delegación específica. Útil para vistas de detalle donde
   * se muestra una sola delegación y se necesita actualización en tiempo real.
   *
   * Re-carga la delegación completa cuando:
   * - Cualquier aprobador vota
   * - La delegación cambia de estado
   * - Se avanza al siguiente step
   * - La delegación es retirada
   *
   * @example
   * ```typescript
   * // Vista de detalle de una delegación específica
   * this.doaService.watchById(currentUser.id, delegationId)
   *   .subscribe(updated => {
   *     this.delegation = updated;
   *     this.checkIfUserCanVote();
   *   });
   * ```
   */
  public watchById(userId: number, id: string): Observable<DoA> {
    return this.pusherService.listen(`private_${userId}`, 'doa').pipe(
      startWith({}),
      mergeMap((event: PusherMessage) => {
        if (!event.data ||
          (event.data.id === id)) return this.getById(id);
      })
    );
  }

  /**
   * Emite un voto (aprobación, rechazo o abstención) en una delegación.
   *
   * @param doa - Objeto DoA de la delegación donde se votará
   * @param decision - Decisión del aprobador:
   *   - accountId: ID de la cuenta que vota (requerido)
   *   - vote: Tipo de voto - 'APPROVED', 'DISAPPROVED', 'ABSTAINED' (requerido)
   *   - comment: Comentario opcional (máx 500 caracteres)
   *
   * @returns Observable con la delegación actualizada después del voto
   *
   * @description
   * Emite un voto en una delegación de autorización. El backend valida:
   * - El accountId pertenece al usuario autenticado (en proxy Laravel)
   * - El accountId está en el step actual de votación
   * - La delegación está en estado PENDING
   * - La delegación no ha vencido (si tiene dueDate)
   * - El account no ha votado previamente
   *
   * **Efectos del voto según tipo:**
   *
   * APPROVED o ABSTAINED:
   * - Registra el voto con timestamp
   * - Avanza al siguiente step (si todos del step actual votaron)
   * - Si era el último step → Estado cambia a APPROVED
   * - Notifica al siguiente aprobador
   * - Emite evento SNS DOA.VOTE.{APPROVED|ABSTAINED}
   *
   * DISAPPROVED:
   * - Estado cambia inmediatamente a DISAPPROVED
   * - Limpia todos los votos pendientes
   * - Notifica a todos los aprobadores
   * - Emite evento SNS DOA.REJECTED
   *
   * Adicionalmente:
   * - Envía push notification en tiempo real via Pusher
   * - Registra track record para auditoría
   * - Actualiza entity track record
   *
   * @throws {400} Account doesn't belong to the user (validación Laravel)
   * @throws {403} Account not authorized to vote on current step
   * @throws {400} Delegation expired
   * @throws {400} Delegation is not PENDING
   *
   * @example
   * ```typescript
   * // Aprobar
   * const decision = new ApproverDecision();
   * decision.accountId = currentAccount.id;
   * decision.vote = 'APPROVED';
   * decision.comment = 'Todo en orden, aprobado según presupuesto';
   *
   * this.doaService.vote(delegation, decision).subscribe(
   *   updated => {
   *     console.log('Voto registrado. Nuevo estado:', updated.status);
   *     console.log('Step actual:', updated.currentVotingStep);
   *   },
   *   error => console.error('Error al votar:', error.message)
   * );
   * ```
   *
   * @example
   * ```typescript
   * // Rechazar
   * const decision = new ApproverDecision();
   * decision.accountId = currentAccount.id;
   * decision.vote = 'DISAPPROVED';
   * decision.comment = 'Monto excede el presupuesto aprobado';
   *
   * this.doaService.vote(delegation, decision).subscribe(
   *   updated => {
   *     console.log('Delegación rechazada:', updated.status); // 'DISAPPROVED'
   *   }
   * );
   * ```
   */
  public vote(doa: DoA, decision: ApproverDecision): Observable<DoA> {
    let url = this.delegationVote
      .replace(":id", doa.id.toString());

    return this.http.post<JSend<{
      delegation: DoA
    }>>(url, decision).pipe(map(response => {
      return plainToInstance(DoA, response.data.delegation);
    }));
  }
}
